import { logger } from './logger';
import {
  canManageLocationRole,
  hasExecutiveLocationAccess,
} from '@shared/role-access';

/**
 * Location-based data utilities for consistent handling across BoltOS
 * Implements the multi-location architecture requirements
 */

export interface LocationFilterOptions {
  selectedLocationIds: string[];
  userLocationIds?: string[];
  includeAll?: boolean;
}

/**
 * Get all locations a user has access to
 * This includes their primary location and any additional locations
 */
export function getUserAllLocations(user: {
  locationId?: string | null;
  locationIds?: string[];
  role: string;
}): string[] {
  const allUserLocations: string[] = [];
  
  // Add primary location if exists
  if (user.locationId) {
    allUserLocations.push(user.locationId);
  }
  
  // Add additional locations
  if (user.locationIds && Array.isArray(user.locationIds)) {
    user.locationIds.forEach(locId => {
      if (!allUserLocations.includes(locId)) {
        allUserLocations.push(locId);
      }
    });
  }
  
  return allUserLocations;
}

/**
 * Check if user has access to view data for selected locations
 */
export function hasLocationAccess(
  selectedLocationIds: string[],
  userLocationIds: string[] | undefined,
  userRole: string,
  userPrimaryLocation?: string | null
): boolean {
  // CEO and ExecutiveTeam can view all locations
  if (hasExecutiveLocationAccess(userRole)) {
    logger.debug('User has executive access to all locations', { userRole });
    return true;
  }

  // Get all user locations including primary
  const allUserLocations = [...(userLocationIds || [])];
  if (userPrimaryLocation && !allUserLocations.includes(userPrimaryLocation)) {
    allUserLocations.push(userPrimaryLocation);
  }

  // If user has no location assignments, they can't view any data
  if (allUserLocations.length === 0) {
    logger.warn('User has no location assignments', { userRole });
    return false;
  }

  // Check if "all" is selected - user can only see their assigned locations
  if (selectedLocationIds.includes('all')) {
    return true; // Will be filtered server-side based on user's actual access
  }

  // Check if user has access to all selected locations
  const hasAccess = selectedLocationIds.every(locationId => 
    allUserLocations.includes(locationId)
  );
  
  if (!hasAccess) {
    logger.debug('User lacks access to some selected locations', {
      selectedLocationIds,
      userLocations: allUserLocations,
      missingAccess: selectedLocationIds.filter(id => !allUserLocations.includes(id))
    });
  }
  
  return hasAccess;
}

/**
 * Get effective location IDs for API queries based on user's access
 */
export function getEffectiveLocationIds(
  selectedLocationIds: string[],
  user: {
    locationId?: string | null;
    locationIds?: string[];
    role: string;
  },
  allLocationIds: string[]
): string[] {
  // Get all locations the user has access to
  const userAccessibleLocations = getUserAllLocations(user);
  
  // CEO and ExecutiveTeam can access all locations
  if (hasExecutiveLocationAccess(user.role)) {
    if (selectedLocationIds.includes('all')) {
      return allLocationIds;
    }
    return selectedLocationIds;
  }
  
  if (selectedLocationIds.includes('all')) {
    // If user selected "all", return only their accessible locations
    return userAccessibleLocations;
  }
  
  // Return intersection of selected and accessible locations
  return selectedLocationIds.filter(id => userAccessibleLocations.includes(id));
}

/**
 * Build API query string for location filtering
 */
export function buildLocationQuery(locationIds: string[]): string {
  if (locationIds.length === 0) return '';
  if (locationIds.includes('all')) return '';
  
  return `locationIds=${locationIds.join(',')}`;
}

/**
 * Aggregate numeric data across multiple locations
 */
export function aggregateNumericData<T extends Record<string, any>>(
  data: T[],
  field: string,
  operation: 'sum' | 'average' | 'count' = 'sum'
): number {
  if (!data || data.length === 0) return 0;

  const values = data.map(item => Number(item[field]) || 0);

  switch (operation) {
    case 'sum':
      return values.reduce((sum, val) => sum + val, 0);
    
    case 'average':
      const sum = values.reduce((sum, val) => sum + val, 0);
      return sum / values.length;
    
    case 'count':
      return values.filter(val => val > 0).length;
    
    default:
      return 0;
  }
}

/**
 * Aggregate status counts across locations
 */
export function aggregateStatusCounts<T extends { status: string }>(
  data: T[]
): Record<string, number> {
  const counts: Record<string, number> = {};
  
  data.forEach(item => {
    counts[item.status] = (counts[item.status] || 0) + 1;
  });
  
  return counts;
}

/**
 * Merge time-based data from multiple locations
 */
export function mergeTimeSeriesData<T extends { date: string; value: number }>(
  datasets: T[][]
): T[] {
  const merged = new Map<string, number>();
  
  datasets.forEach(dataset => {
    dataset.forEach(item => {
      const existing = merged.get(item.date) || 0;
      merged.set(item.date, existing + item.value);
    });
  });
  
  return Array.from(merged.entries())
    .map(([date, value]) => ({ date, value } as T))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

/**
 * Group data by location for breakdown views
 */
export function groupByLocation<T extends { locationId: string }>(
  data: T[]
): Record<string, T[]> {
  return data.reduce((groups, item) => {
    const locationId = item.locationId;
    if (!groups[locationId]) {
      groups[locationId] = [];
    }
    groups[locationId].push(item);
    return groups;
  }, {} as Record<string, T[]>);
}

/**
 * Calculate percentage metrics across locations
 */
export function calculatePercentage(value: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((value / total) * 100);
}

/**
 * Format location display names
 */
export function getLocationDisplayName(
  selectedLocationIds: string[],
  locations: Array<{ id: string; name: string }>
): string {
  if (selectedLocationIds.includes('all')) {
    return 'All Locations';
  }
  
  if (selectedLocationIds.length === 0) {
    return 'No Location Selected';
  }
  
  if (selectedLocationIds.length === 1) {
    const location = locations.find(loc => loc.id === selectedLocationIds[0]);
    return location?.name || 'Unknown Location';
  }
  
  if (selectedLocationIds.length === locations.length) {
    return 'All Locations';
  }
  
  return `${selectedLocationIds.length} Locations`;
}

/**
 * Check if data should be aggregated based on selection
 */
export function shouldAggregateData(selectedLocationIds: string[]): boolean {
  return selectedLocationIds.length > 1 || selectedLocationIds.includes('all');
}

/**
 * Filter data based on location selection and user access
 */
export function filterByLocation<T extends { locationId: string }>(
  data: T[],
  selectedLocationIds: string[],
  userAccessibleLocations?: string[]
): T[] {
  // If specific locations are selected, filter by them
  if (!selectedLocationIds.includes('all')) {
    return data.filter(item => selectedLocationIds.includes(item.locationId));
  }
  
  // If "all" is selected and user has limited access, filter by their locations
  if (userAccessibleLocations && userAccessibleLocations.length > 0) {
    return data.filter(item => userAccessibleLocations.includes(item.locationId));
  }
  
  // Otherwise return all data
  return data;
}

/**
 * Check if a user can manage data at a specific location
 * Team Leads and above can manage their assigned locations
 */
export function canManageLocation(
  user: {
    role: string;
    locationId?: string | null;
    locationIds?: string[];
  },
  locationId: string
): boolean {
  // CEO and ExecutiveTeam can manage all locations
  if (hasExecutiveLocationAccess(user.role)) {
    return true;
  }
  
  // Team Leads and above can manage their assigned locations
  if (!canManageLocationRole(user.role)) {
    return false;
  }
  
  // Check if location is in user's assigned locations
  const userLocations = getUserAllLocations(user);
  return userLocations.includes(locationId);
}

/**
 * Log location-based query for debugging
 */
export function logLocationQuery(
  module: string,
  selectedLocationIds: string[],
  resultCount: number
): void {
  logger.debug(`${module} location query`, {
    selectedLocationIds,
    isMultiLocation: selectedLocationIds.length > 1,
    isAllLocations: selectedLocationIds.includes('all'),
    resultCount
  });
}
